import React from 'react';
import SearchablePicker from './SearchablePicker.tsx';
import { Product } from 'utils/grocyApi.ts';
import { useProductsQuery, useStockQuery } from 'hooks/useQueries.ts';
import './ProductPicker.css';

export interface ProductPickerProps {
  // Controlled search
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  // Selection
  onSelect: (product: Product) => void;
  // Create new option
  showCreateNew?: boolean;
  onCreateNew?: (term: string) => void;
  // UI behaviour
  autoFocus?: boolean;
  showList?: boolean;
  // Event handlers
  onInputFocus?: () => void;
  // Custom sorting for parent product use case
  sortMode?: 'default' | 'parent-selection';
  // Filter options
  onlyInStock?: boolean;
}

const ProductPicker: React.FC<ProductPickerProps> = ({
  searchTerm,
  onSearchTermChange,
  onSelect,
  showCreateNew = false,
  onCreateNew,
  autoFocus,
  showList = true,
  onInputFocus,
  sortMode = 'default',
  onlyInStock = false,
}) => {
  const { data: products = [] } = useProductsQuery();
  const { data: stock = [] } = useStockQuery();

  const stockMap = React.useMemo(() => {
    const map: Record<number, number> = {};
    stock.forEach((s: { product_id: number; amount: number }) => {
      map[s.product_id] = s.amount;
    });
    return map;
  }, [stock]);

  // Map to track which products have children (other products pointing to them as parent)
  const hasChildrenMap = React.useMemo(() => {
    const map: Record<number, boolean> = {};
    products.forEach((product) => {
      if (product.parent_product_id) {
        map[product.parent_product_id] = true;
      }
    });
    return map;
  }, [products]);

  return (
    <div className="product-picker">
      <SearchablePicker<Product>
        items={products}
        getKey={(p) => p.id}
        onSelect={onSelect}
        searchTerm={searchTerm}
        onSearchTermChange={onSearchTermChange}
        onInputFocus={onInputFocus}
        filter={(p, term) => {
          // First check if product is in stock (if onlyInStock is enabled)
          if (onlyInStock) {
            const stockAmount = stockMap[p.id] || 0;
            if (stockAmount <= 0) return false;
          }
          
          // Then check if product matches search term
          return p.name.toLowerCase().includes(term.toLowerCase()) ||
            (p.description?.toLowerCase().includes(term.toLowerCase()) ?? false);
        }}
        sort={(a, b) => {
          if (sortMode === 'parent-selection') {
            // Custom sorting for parent product selection:
            // 1. no_own_stock = 1 first
            // 2. products that have children second  
            // 3. rest by name
            const aIsNoOwnStock = a.no_own_stock === 1;
            const bIsNoOwnStock = b.no_own_stock === 1;
            const aHasChildren = hasChildrenMap[a.id] || false;
            const bHasChildren = hasChildrenMap[b.id] || false;
            
            if (aIsNoOwnStock && !bIsNoOwnStock) return -1;
            if (!aIsNoOwnStock && bIsNoOwnStock) return 1;
            
            if (aIsNoOwnStock && bIsNoOwnStock) {
              return a.name.localeCompare(b.name);
            }
            
            if (aHasChildren && !bHasChildren) return -1;
            if (!aHasChildren && bHasChildren) return 1;
            
            return a.name.localeCompare(b.name);
          } else {
            // Default sorting: no_own_stock first, then by name
            if (a.no_own_stock === 1 && b.no_own_stock !== 1) return -1;
            if (a.no_own_stock !== 1 && b.no_own_stock === 1) return 1;
            return a.name.localeCompare(b.name);
          }
        }}
        renderItem={(product) => {
          const stockAmount = stockMap[product.id] || 0;
          const hasChildren = hasChildrenMap[product.id] || false;
          return (
            <>
              <div className="product-picker__name">
                {product.name}
                {product.no_own_stock === 1 && (
                  <span className="product-picker__badge product-picker__badge--parent">Parent</span>
                )}
                {sortMode === 'parent-selection' && hasChildren && product.no_own_stock !== 1 && (
                  <span className="product-picker__badge product-picker__badge--has-children">Has variants</span>
                )}
                {stockAmount > 0 && (
                  <span className="product-picker__stock">({stockAmount} in stock)</span>
                )}
              </div>
              {product.description && (
                <div className="product-picker__description">{product.description}</div>
              )}
            </>
          );
        }}
        showCreateNew={showCreateNew}
        onCreateNew={onCreateNew}
        createNewLabel={(term) => `+ Add "${term}" as new product`}
        label={undefined}
        placeholder="Search for product..."
        autoFocus={autoFocus}
        fieldContainerClassName="product-picker__field"
        inputClassName="product-picker__input"
        listClassName="product-picker__list"
        itemClassName="product-picker__item"
        newItemClassName="product-picker__item product-picker__item--new"
        showList={showList}
      />
    </div>
  );
};

export default ProductPicker;


